/**
 * Centralized, typed TanStack Query key factory for the `apps/app` workspace.
 *
 * Every builder returns an `as const` tuple IDENTICAL (same elements, order, and
 * string values, including computed parts) to the literal it replaces, so cache
 * identity is unchanged. Builders that take parameters reproduce the exact
 * original expression.
 *
 * Core-package keys (`profile`, `userStats`, `categories`) are routed through
 * `coreQueryKeys` so they stay in sync across apps.
 */
import { coreQueryKeys } from "@lyder-talk/core";

export interface SharedSessionsParams {
  page: number;
  pageSize: number;
  search?: string;
  category?: string;
  sortBy?: string;
}

export const queryKeys = {
  // ---- Core (shared) keys ----
  profile: coreQueryKeys.profile,
  userStats: coreQueryKeys.userStats,
  categories: coreQueryKeys.categories,
  /** The caller's own email notification preferences (account settings). */
  emailPreferences: (userId: string | null | undefined) =>
    ["email-preferences", userId] as const,

  // ---- Practice sessions / history ----
  practiceSessions: {
    all: ["practice-sessions"] as const,
    list: (userId: string | null, loadedCount: number) =>
      ["practice-sessions", userId, loadedCount] as const,
    /** Bare prefix form for invalidation: ["practice-sessions", userId] */
    byUser: (userId: string | null | undefined) =>
      ["practice-sessions", userId] as const,
  },
  practiceHistory: {
    all: ["practice-history"] as const,
    /** Bare prefix form for invalidation: ["practice-history", userId] */
    byUser: (userId: string | null | undefined) =>
      ["practice-history", userId] as const,
    list: (
      userId: string | null,
      page: number,
      pageSize: number,
      search: string,
      categoryId: string | null
    ) =>
      ["practice-history", userId, page, pageSize, search, categoryId] as const,
  },
  recentPractice: {
    all: ["recent-practice"] as const,
    list: (userId: string | null | undefined, limit: number) =>
      ["recent-practice", userId, limit] as const,
  },
  recentStudied: {
    all: ["recent-studied"] as const,
    list: (userId: string | null | undefined, limit: number) =>
      ["recent-studied", userId, limit] as const,
  },
  speakingRecordings: {
    all: ["speaking-recordings"] as const,
    byUser: (userId: string | null | undefined) =>
      ["speaking-recordings", userId] as const,
  },

  // ---- Vocabulary ----
  savedVocabulary: {
    all: ["saved-vocabulary"] as const,
    byUser: (userId: string | null | undefined) =>
      ["saved-vocabulary", userId] as const,
    /** Distinct key for the collection word-picker's own (capped, subset-column)
     *  fetch — avoids colliding with the full-row `.all`/`.byUser` caches while
     *  still being caught by `.all`-prefix invalidations. */
    picker: (userId: string | null | undefined) =>
      ["saved-vocabulary", "picker", userId] as const,
    /** Infinite paginated deck list (server-side filtered/sorted).
     * `pageSize` is part of the key: consumers use different page sizes (deck
     * vs. sidebar preview) and must not share one infinite-query cache entry. */
    list: (
      userId: string | null | undefined,
      search: string,
      filter: string,
      certLevel: string,
      wordType: string,
      calendarDate: string | null,
      tier: string | null,
      pageSize: number
    ) =>
      ["saved-vocabulary", "list", userId, search, filter, certLevel, wordType, calendarDate, tier, pageSize] as const,
    /** Deck facets: cert levels/types + status + tier counts. */
    facets: (userId: string | null | undefined) =>
      ["saved-vocabulary", "facets", userId] as const,
    /** Lightweight review-date rows for the calendar sidebar. */
    calendar: (userId: string | null | undefined) =>
      ["saved-vocabulary", "calendar", userId] as const,
    /** Just the saved words (one column) — used to highlight deck words
     *  inside example sentences. */
    words: (userId: string | null | undefined) =>
      ["saved-vocabulary", "words", userId] as const,
    /** Word + definition + type, paged — the dictionary's Saved Words tab.
     *  A distinct key so it never collides with the full-row deck cache it
     *  replaced there, while still matching `.all`-prefix invalidations. */
    wordList: (userId: string | null | undefined, pageSize: number) =>
      ["saved-vocabulary", "word-list", userId, pageSize] as const,
    /** Only the words that are due for review, with the columns the reminder
     *  modal renders. Deliberately not the full deck: the provider mounts on
     *  every page, and the deck payload is ~1MB at 1,000 words. */
    dueItems: (userId: string | null | undefined) =>
      ["saved-vocabulary", "due-items", userId] as const,
  },
  savedVocabularyStats: {
    all: ["saved-vocabulary-stats"] as const,
    byUser: (userId: string | null | undefined) =>
      ["saved-vocabulary-stats", userId] as const,
  },
  dueReviewGroups: {
    all: ["due-review-groups"] as const,
    byUser: (userId: string | null | undefined) =>
      ["due-review-groups", userId] as const,
  },
  userNotifications: {
    all: ["user-notifications"] as const,
    /** Unread badge count — a HEAD count, never the notification rows. */
    unreadCount: (userId: string | null | undefined) =>
      ["user-notifications", "unread-count", userId] as const,
  },
  vocabularySaveStatus: {
    all: ["vocabulary-save-status"] as const,
    byWord: (word: string) => ["vocabulary-save-status", word] as const,
  },
  vocabularySet: {
    all: ["vocabulary-set"] as const,
    byId: (id: string | null | undefined) => ["vocabulary-set", id] as const,
  },
  vocabularySetItems: {
    all: ["vocabulary-set-items"] as const,
    byId: (id: string | null | undefined) =>
      ["vocabulary-set-items", id] as const,
  },
  vocabularySetSaved: {
    byId: (id: string | null | undefined, userId: string | null | undefined) =>
      ["vocabulary-set-saved", id, userId] as const,
  },
  vocabularySetSavedWords: {
    /** Specific form with words hash */
    byId: (
      id: string | null | undefined,
      userId: string | null | undefined,
      wordsKey: string
    ) => ["vocabulary-set-saved-words", id, userId, wordsKey] as const,
    /** Prefix form for invalidation: [..., id, userId] */
    prefix: (
      id: string | null | undefined,
      userId: string | null | undefined
    ) => ["vocabulary-set-saved-words", id, userId] as const,
  },
  vocabularySetProgress: {
    byId: (id: string | null | undefined, userId: string | null | undefined) =>
      ["vocabulary-set-progress", id, userId] as const,
  },
  vocabularySetsProgress: {
    list: (ids: string[]) => ["vocabulary-sets-progress", [...ids].sort()] as const,
  },
  collectionsWidgetCounts: {
    byUser: (userId: string | null | undefined) =>
      ["collections-widget-counts", userId] as const,
  },
  vocabularyLeaderboard: {
    list: (page: number, pageSize: number) =>
      ["vocabulary-leaderboard", page, pageSize] as const,
  },
  /** Folded-in former `vocabularySetKeys` factory (collection lists). */
  vocabularySets: {
    all: ["vocabulary-sets"] as const,
    list: (
      page: number,
      pageSize: number,
      search: string,
      filter: string,
      sort: string,
      ownerId: string | null
    ) =>
      ["vocabulary-sets", "list", page, pageSize, search, filter, sort, ownerId] as const,
    overview: (search: string, ownerId: string | null) =>
      ["vocabulary-sets", "overview", search, ownerId] as const,
    auto: (limit: number) => ["vocabulary-sets", "auto", limit] as const,
    topic: (sessionId: string) => ["vocabulary-sets", "topic", sessionId] as const,
    mine: (userId: string | null | undefined) =>
      ["vocabulary-sets", "mine", userId] as const,
    community: (userId: string | null | undefined) =>
      ["vocabulary-sets", "community", userId] as const,
    progress: (ids: string[]) =>
      ["vocabulary-sets-progress", [...ids].sort()] as const,
  },

  // ---- Notes ----
  notes: {
    /** Current user's personal ("own") notes — ["notes", "personal", userId] */
    personal: (userId: string | null | undefined) =>
      ["notes", "personal", userId] as const,
    /** Current user's vocabulary notes — ["notes", "vocabulary", userId] */
    vocabulary: (userId: string | null | undefined) =>
      ["notes", "vocabulary", userId] as const,
   },

  // ---- Study groups ----
  studyGroups: {
    all: ["study-groups"] as const,
    public: (userId?: string | null) =>
      userId === undefined
        ? (["study-groups", "public"] as const)
        : (["study-groups", "public", userId] as const),
    mine: (userId?: string | null) =>
      userId === undefined
        ? (["study-groups", "mine"] as const)
        : (["study-groups", "mine", userId] as const),
    mineShare: (userId: string | null | undefined) =>
      ["study-groups", "mine-share", userId] as const,
    detail: (groupId: string, userId?: string | null) =>
      userId === undefined
        ? (["study-groups", "detail", groupId] as const)
        : (["study-groups", "detail", groupId, userId] as const),
    members: (groupId: string) => ["study-groups", "members", groupId] as const,
    topicOfDay: (groupId: string) =>
      ["study-groups", "topic-of-day", groupId] as const,
    collectionOfDay: (groupId: string) =>
      ["study-groups", "collection-of-day", groupId] as const,
    sharedSessions: (groupId: string, params?: SharedSessionsParams) =>
      params
        ? (["study-groups", "shared-sessions", groupId, params] as const)
        : (["study-groups", "shared-sessions", groupId] as const),
    sharedSessionsAll: () => ["study-groups", "shared-sessions"] as const,
    sharedSessionsInfinite: (groupId: string, params?: Omit<SharedSessionsParams, "page">) =>
      params
        ? (["study-groups", "shared-sessions", "infinite", groupId, params] as const)
        : (["study-groups", "shared-sessions", "infinite", groupId] as const),
    /**
     * The picker's own page of a group's shared topics.
     *
     * Deliberately a separate key from `sharedSessionsInfinite`: the inline grid
     * is paginated and filtered for browsing, while the Share / Set Plan / Set
     * Today dialogs need their own full, searchable list. Reusing the grid's key
     * meant a dialog could only ever see the grid's first page — 12 topics in a
     * group that shared 60.
     */
    sharedSessionsPicker: (
      groupId: string,
      params: { search?: string; pageSize: number }
    ) => ["study-groups", "shared-sessions", "picker", groupId, params] as const,
    /**
     * Every shared session id in the group, unpaginated. The pickers use it to
     * flag "already shared" rows, which the paginated grid list cannot answer.
     */
    sharedSessionIds: (groupId: string) =>
      ["study-groups", "shared-sessions", "ids", groupId] as const,
    sharedCollections: (groupId: string) =>
      ["study-groups", "shared-collections", groupId] as const,
    activities: (groupId: string) =>
      ["study-groups", "activities", groupId] as const,
    notifications: (userId: string | null | undefined) =>
      ["study-groups", "notifications", userId] as const,
    practicePlans: (groupId: string, yearMonth?: string) =>
      yearMonth === undefined
        ? (["study-groups", "practice-plans", groupId] as const)
        : (["study-groups", "practice-plans", groupId, yearMonth] as const),
  },

  // ---- Gamification ----
  gamification: {
    byUser: (userId: string | null | undefined) =>
      ["gamification", userId] as const,
  },
  streakCalendar: {
    byUser: (userId: string | null | undefined, days?: number) =>
      days === undefined
        ? (["streak-calendar", userId] as const)
        : (["streak-calendar", userId, days] as const),
  },
  streakMilestones: {
    all: () => ["streak-milestones-all"] as const,
    byUser: (userId: string | null | undefined) =>
      ["streak-milestones", userId] as const,
  },
  dailyChallenges: {
    byUser: (userId: string | null | undefined) =>
      ["daily-challenges", userId] as const,
  },
  dailyChallengeSingle: {
    byUser: (userId: string | null | undefined) =>
      ["daily-challenge-single", userId] as const,
  },
  dailyTopic: {
    all: () => ["daily-topic"] as const,
  },
  groupTopicOfDay: {
    byUser: (userId: string | null | undefined) =>
      ["group-topic-of-day", userId] as const,
  },

  // ---- Leaderboards ----
  /** Unified metric + period board (migration 0154). */
  leaderboardRankings: {
    list: (metric: string, period: string, pageSize: number) =>
      ["leaderboard-rankings", metric, period, pageSize] as const,
  },
  streakLeaderboard: {
    list: (page: number, pageSize: number) =>
      ["streak-leaderboard", page, pageSize] as const,
  },
  timeLeaderboard: {
    list: (page: number, pageSize: number) =>
      ["time-leaderboard", page, pageSize] as const,
  },
  xpLeaderboard: {
    list: (page: number, pageSize: number) =>
      ["xp-leaderboard", page, pageSize] as const,
  },
  leaderboardTopics: {
    list: (page: number, pageSize: number, category: string) =>
      ["leaderboard-topics", page, pageSize, category] as const,
  },

  // ---- Videos ----
  videos: {
    all: ["videos"] as const,
    list: (
      categoryId: string | null,
      certLevel: string | null,
      page: number,
      pageSize: number,
      searchTerm: string
    ) =>
      ["videos", categoryId, certLevel, page, pageSize, searchTerm] as const,
    libraryStats: () => ["videos", "library-stats"] as const,
    /**
     * Caption-level ("in context") search. Keyed on the trimmed query so a
     * leading space or a re-typed capital does not start a second request.
     */
    /**
     * ["videos","caption-search",query] (bare) and
     * ["videos","caption-search",query,source] — two different corpora, so two
     * different answers for the same word.
     */
    captionSearch: (query: string, source?: string) =>
      source === undefined
        ? (["videos", "caption-search", query] as const)
        : (["videos", "caption-search", query, source] as const),
    /**
     * The single best clip for one word — what a topic's vocabulary asks for.
     * Separate from `captionSearch` because that key belongs to a paged result
     * set; a one-line lookup never collides with it, and the deck's up to ten
     * lookups each get their own cache entry.
     */
    captionTopClip: (query: string) => ["videos", "caption-top", query] as const,
  },
  video: {
    byId: (id: string) => ["video", id] as const,
  },
  videoTranscript: {
    byId: (id: string) => ["video-transcript", id] as const,
  },
  videoReviewItems: {
    byUser: (userId: string | null | undefined) =>
      ["video-review-items", userId] as const,
  },
  videoPracticeProgress: {
    list: (videoIds: string[], userId: string | null | undefined) =>
      ["video-practice-progress", videoIds, userId] as const,
  },
  videoRequests: {
    all: ["video-requests"] as const,
  },
  adminVideos: {
    all: ["admin-videos"] as const,
    byPlatform: (platform: string) => ["admin-videos", platform] as const,
  },

  // ---- Comments ----
  comments: {
    bySession: (sessionId: string) => ["comments", sessionId] as const,
  },
  replyCounts: {
    /** Prefix form for invalidation: ["reply-counts", sessionId] */
    bySession: (sessionId: string) => ["reply-counts", sessionId] as const,
    /** Specific form keyed by the loaded top-level comment ids so counts
     *  refetch when the visible comment set changes. */
    withIds: (sessionId: string, idsKey: string) =>
      ["reply-counts", sessionId, idsKey] as const,
  },
  commentReactions: {
    byComment: (commentId: string) => ["comment-reactions", commentId] as const,
  },

  // ---- Sessions / engagement / topics ----
  sessionEngagement: {
    bySession: (sessionId: string) => ["session-engagement", sessionId] as const,
  },
  sessionBookmark: {
    bySession: (sessionId: string) => ["session-bookmark", sessionId] as const,
  },
  bookmarkedSessions: {
    all: ["bookmarked-sessions"] as const,
  },
  relatedSessions: {
    list: (sessionId: string, category: string | null | undefined) =>
      ["related-sessions", sessionId, category] as const,
  },
  sharedSession: {
    bySlug: (titleSlug: string) => ["shared-session", titleSlug] as const,
  },
  sharedTopics: {
    groups: (sessionId: string) => ["shared-topics", "groups", sessionId] as const,
  },
  sharedCollections: {
    groups: (id: string) => ["shared-collections", "groups", id] as const,
  },
  topicTime: {
    bySession: (sessionId: string) => ["topic-time", sessionId] as const,
  },
  topicTimeList: {
    list: (userId: string | null | undefined, sortBy: string) =>
      ["topic-time-list", userId, sortBy] as const,
  },
  myTopicSession: {
    byTopic: (topicId: string) => ["my-topic-session", topicId] as const,
  },

  // ---- Public / community ----
  publicSessions: {
    list: (
      page: number,
      pageSize: number,
      searchTerm: string,
      category: string,
      sortBy: string
    ) => ["public-sessions", page, pageSize, searchTerm, category, sortBy] as const,
  },
  communityStats: {
    byCount: (count: number | undefined) =>
      ["community-stats", count] as const,
  },
  randomQuestions: {
    all: ["random-questions"] as const,
    /** Pool of questions pulled from public practice sessions, optionally by difficulty. */
    list: (difficulty: string | null) =>
      ["random-questions", difficulty] as const,
  },

  // ---- Users ----
  userProfile: {
    byId: (userId: string | null) => ["user-profile", userId] as const,
  },
  userProfiles: {
    byIds: (idsKey: string) => ["user-profiles", idsKey] as const,
  },
  userVideoPractice: {
    byVideo: (
      videoId: string | null | undefined,
      mode: string,
      userId: string | null | undefined
    ) => ["user-video-practice", videoId, mode, userId] as const,
  },
  userVideoPracticeCounts: {
    byVideo: (
      videoId: string | null | undefined,
      mode: string,
      userId: string | null | undefined
    ) => ["user-video-practice-counts", videoId, mode, userId] as const,
  },

  // ---- AI / practice vocabulary ----
  aiConversations: {
    all: () => ["ai-conversations"] as const,
  },
  practiceVocabulary: {
    list: (
      mode: string,
      sessionId: string | null | undefined,
      setId: string | null | undefined,
      ids: string[] | null | undefined,
      userId: string | null | undefined
    ) => ["practice-vocabulary", mode, sessionId, setId, ids, userId] as const,
  },

  // ---- Admin ----
  admin: {
    auth: () => ["admin", "auth"] as const,
    settings: () => ["admin", "settings"] as const,
  },
} as const;
